import type { ChildWithProfile, Collection, ContentItem, Language } from '@kidshub/contracts';
import { type CSSProperties, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n/use-locale.js';
import { apiClient } from '../lib/api-client.js';
import { formatClock } from '../lib/duration.js';
import { toErrorKey } from '../lib/error-code.js';
import { Button, Icon } from '../ui/kit.js';
import { ApprovalChips } from './approval-chips.js';
import { CollectionChips } from './collection-chips.js';
import { RenameField } from './rename-field.js';
import { SourceBadge } from './source-badge.js';
import { suggestTitle } from './title-suggestion.js';

/**
 * One item in the library.
 *
 * The card carries the source, the length and — in a fixed place, always — the
 * answer to who can see it. Filing it into collections and removing it sit
 * behind "Edit": they are done far less often than approving, and a card that
 * showed every control at once buried the one that matters.
 *
 * Approval alone does not put an item on a tablet. The tablet's home screen is
 * made of collections, so an item approved for a child but in no collection
 * switched on for them is approved and still invisible. The card says so,
 * because that is exactly the gap a parent falls into and then reports as a bug.
 *
 * Removing is a two-step action for the same reason revoking a device is: it
 * is destructive, it is occasionally the right thing, and it should not happen
 * to a passing click.
 */

interface ContentCardProps {
  item: ContentItem;
  familyChildren: ChildWithProfile[];
  collections: Collection[];
  /** The felt behind an item with no picture of its own, as a CSS colour. */
  felt: string;
  nativeLanguages: readonly Language[];
  onChange: (item: ContentItem) => void;
  onRemoved: (item: ContentItem) => void;
  onCollectionsChanged: () => void;
}

export const ContentCard = ({
  item,
  familyChildren,
  collections,
  felt,
  nativeLanguages,
  onChange,
  onRemoved,
  onCollectionsChanged,
}: ContentCardProps) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const manageId = useId();
  const [isManaging, setIsManaging] = useState(false);
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const remove = async () => {
    setErrorKey(null);
    setIsBusy(true);

    try {
      await apiClient.catalog.remove({ contentItemId: item.id });
      onRemoved(item);
    } catch (error) {
      setErrorKey(toErrorKey(error));
      setIsBusy(false);
    }
  };

  const homes = collections.filter((collection) => collection.itemIds.includes(item.id));
  const isRenamed = item.originalTitle !== null && item.title !== item.originalTitle;

  const rename = async (title: string) => {
    setErrorKey(null);
    setIsBusy(true);
    try {
      onChange(await apiClient.catalog.rename({ contentItemId: item.id, title }));
    } catch (error) {
      setErrorKey(toErrorKey(error));
    } finally {
      setIsBusy(false);
    }
  };
  const listFormat = (names: string[], type: 'conjunction' | 'unit') =>
    new Intl.ListFormat(locale, { type, style: type === 'unit' ? 'narrow' : 'long' }).format(names);

  // Approved, but not on any shelf this child's tablet draws.
  const stranded = familyChildren
    .filter((entry) => item.approvedForChildIds.includes(entry.child.id))
    .filter(
      (entry) =>
        !homes.some((collection) => entry.profile.enabledCollectionIds.includes(collection.id)),
    )
    .map((entry) => entry.child.name);

  return (
    <article className="item">
      <div className="poster" style={{ '--pc': felt } as CSSProperties} aria-hidden="true">
        {item.posterUrl === null ? (
          <span className="lib-poster-mark">
            <Icon name={item.contentType === 'GAME' ? 'yarn' : 'play'} />
          </span>
        ) : (
          <img className="lib-poster-img" src={item.posterUrl} alt="" loading="lazy" />
        )}
      </div>

      <div className="item-body">
        <h3 className="item-t">{item.title}</h3>
        {isRenamed ? (
          <p className="lib-orig" title={item.originalTitle ?? undefined}>
            {t('collectionPage.original', { title: item.originalTitle })}
          </p>
        ) : null}
        <p className="item-m lib-meta">
          {item.durationSeconds === null ? null : (
            <span className="num">{formatClock(item.durationSeconds)}</span>
          )}
          <span>
            {homes.length === 0
              ? t('library.card.noCollection')
              : listFormat(
                  homes.map((collection) => collection.title),
                  'unit',
                )}
          </span>
        </p>
        <div className="lib-badges">
          <SourceBadge
            providerId={item.providerId}
            contentType={item.contentType}
            audioLanguage={item.audioLanguage}
            nativeLanguages={nativeLanguages}
          />
        </div>
      </div>

      <div className="lib-item-foot">
        <ApprovalChips item={item} familyChildren={familyChildren} onChange={onChange} />

        {stranded.length === 0 ? null : (
          <p className="lib-warn">
            <Icon name="alert" small />
            {t('library.card.notOnTablet', {
              names: listFormat(stranded, 'conjunction'),
            })}
          </p>
        )}

        <Button
          variant="ghost"
          small
          className="lib-manage"
          aria-expanded={isManaging}
          aria-controls={manageId}
          onClick={() => setIsManaging(!isManaging)}
        >
          {t('library.card.manage')}
          <Icon name="chev" small />
        </Button>

        <div className="lib-manage-body" id={manageId} hidden={!isManaging}>
          {isManaging ? (
            <RenameField
              // Remounted per title so the field starts from the saved name.
              key={item.title}
              item={item}
              suggestion={
                isRenamed
                  ? null
                  : suggestTitle(
                      item.title,
                      homes.map((collection) => collection.title),
                      locale,
                    )
              }
              isBusy={isBusy}
              onSave={(title) => void rename(title)}
            />
          ) : null}
          <CollectionChips item={item} collections={collections} onChanged={onCollectionsChanged} />

          {errorKey === null ? null : (
            <p className="help err" role="alert">
              {t(errorKey)}
            </p>
          )}

          {isConfirmingRemove ? (
            <div className="lib-actions">
              <Button
                variant="danger"
                small
                className="btn-danger-solid"
                disabled={isBusy}
                onClick={() => void remove()}
              >
                {t('library.removeConfirm')}
              </Button>
              <Button variant="ghost" small onClick={() => setIsConfirmingRemove(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          ) : (
            <div className="lib-actions">
              <Button variant="danger" small onClick={() => setIsConfirmingRemove(true)}>
                {t('library.remove')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};
